<template>
  <div class="action-bar__mobile-row" data-testid="pdp-action-bar-mobile">
    <AlertBox
      v-if="maintenanceMode"
      class="mt-4 maintenance-mode"
      icon="attention"
      :style="'danger'"
    >
      <template #default>
        <DictionaryRichText dictionary-key="maintenanceMode.text" />
      </template>
    </AlertBox>
    <template v-else>
      <Button
        v-if="displayAddToCartButton"
        style-type="attraction"
        icon-type="icon-left"
        icon-name="add-shopping-cart"
        :icon-scale="1.4"
        size="medium"
        :active-state="isInitialLoading ? 'loading' : addToCartButtonState"
        :disabled="isInitialLoading || addToCartButtonDisabled"
        :states="{
          loading: {
            hideText: false,
            text:
              $textDictionary['pdp.addToCart.buttonState.loading'] ??
              'pdp.addToCart.buttonState.loading',
            ariaText:
              $textDictionary['pdp.addToCart.buttonState.loading'] ??
              'pdp.addToCart.buttonState.loading',
            resetDelay: -1
          },
          error: {
            hideText: false,
            text:
              $textDictionary['pdp.addToCart.buttonState.error'] ??
              'pdp.addToCart.buttonState.error',
            ariaText:
              $textDictionary['pdp.addToCart.buttonState.error'] ??
              'pdp.addToCart.buttonState.error',
            resetDelay: 2000
          },
          success: {
            hideText: false,
            text:
              $textDictionary['pdp.addToCart.buttonState.success'] ??
              'pdp.addToCart.buttonState.success',
            ariaText:
              $textDictionary['pdp.addToCart.buttonState.success'] ??
              'pdp.addToCart.buttonState.success',
            resetDelay: 2000
          }
        }"
        @click="
          $emit(
            'add-to-cart',
            loggedIn ? CartTypes.CUSTOMER_CART : CartTypes.CART
          )
        "
      >
        {{ addToCartMobile }}
      </Button>

      <Button
        v-if="displayRequestQuoteButton"
        style-type="attraction"
        size="medium"
        :active-state="requestQuoteButtonState"
        :disabled="requestQuoteButtonDisabled"
        :states="{
          loading: {
            hideText: false,
            text:
              $textDictionary['pdp.requestButton.buttonState.loading'] ??
              'pdp.requestButton.buttonState.loading',
            ariaText:
              $textDictionary['pdp.requestButton.buttonState.loading'] ??
              'pdp.requestButton.buttonState.loading',
            resetDelay: -1
          },
          error: {
            hideText: false,
            text:
              $textDictionary['pdp.requestButton.buttonState.error'] ??
              'pdp.requestButton.buttonState.error',
            ariaText:
              $textDictionary['pdp.requestButton.buttonState.error'] ??
              'pdp.requestButton.buttonState.error',
            resetDelay: 2000
          },
          success: {
            hideText: false,
            text:
              $textDictionary['pdp.requestButton.buttonState.success'] ??
              'pdp.requestButton.buttonState.success',
            ariaText:
              $textDictionary['pdp.requestButton.buttonState.success'] ??
              'pdp.requestButton.buttonState.success',
            resetDelay: 2000
          }
        }"
        @click="$emit('add-to-cart', CartTypes.REQUEST_CART)"
      >
        {{
          $textDictionary['pdp.requestButton.label'] ??
          'pdp.requestButton.label'
        }}
      </Button>

      <Button
        style-type="link"
        size="small"
        :expand="true"
        icon-name="arrow-up-bold"
        icon-type="icon-left"
        @click="$emit('show-options')"
      >
        {{
          $textDictionary['pdp.showOptions.label'] ?? 'pdp.showOptions.label'
        }}
      </Button>
    </template>
  </div>
</template>

<script setup lang="ts">
import { AlertBox, Button } from '@hypercodestudio/basler-components';
import type { BtnStateType } from '@hypercodestudio/basler-components';

import { type CartType, CartTypes } from '~/stores/cartStore/model';

const { $textDictionary, $globalPageSettings } = useNuxtApp();

const maintenanceMode = computed(() => {
  return $globalPageSettings.value?.shopConfig?.maintenanceMode;
});

interface Props {
  isInitialLoading: boolean;
  addToCartButtonState: BtnStateType;
  addToCartButtonDisabled: boolean;
  requestQuoteButtonState: BtnStateType;
  requestQuoteButtonDisabled: boolean;
  priceText: string;
  loggedIn: boolean;
  displayAddToCartButton: boolean;
}

const props = defineProps<Props>();

/**
 * For request-only products the quote button is the primary CTA and must be
 * reachable without opening the options first (DBP-4312). When the product is
 * buyable, add-to-cart stays primary and the quote button lives in the
 * expanded options.
 */
const displayRequestQuoteButton = computed(
  () => !props.isInitialLoading && !props.displayAddToCartButton
);

const addToCartMobile = computed(() => {
  return (
    $textDictionary.value['pdp.addToCartMobile.label']?.replace(
      '{price}',
      String(props.priceText)
    ) ?? 'pdp.addToCartMobile.label'
  );
});

defineEmits<{
  'add-to-cart': [type: CartType];
  'show-options': [];
}>();
</script>

<style scoped></style>
